import { createSlice } from '@reduxjs/toolkit';
import {
  JOB_STATUS,
  STATUS_FILTER,
} from '@/components/Company/importer/constants';
import { sumFileRecordCount } from '@/components/Company/importer/uploadResults';
import {
  applyTargetFieldChange,
  ignoreReviewRows,
} from '@/components/Company/importer/mappingModel';
import { parseImportSettingsFromJob } from '@/components/Company/importer/importSettings';
import {
  emptyCrmInstances,
  primaryCrmEntity,
} from '@/components/Company/importer/crmInstances';
import { isCrmMode } from '@/components/Company/importer/wizard/importerFlow';
import initialState, { initialMappingState } from './initialState';
import {
  fetchJobsAsync,
  retryJobAsync,
  downloadContactsExportAsync,
  uploadFilesAsync,
  startImportAsync,
  removeUploadedFileAsync,
  fetchJobLiveInfoAsync,
  fetchMappingAsync,
  saveMappingAsync,
  fetchValueMappingsAsync,
  saveAgentMappingsAsync,
  saveLeadStatusMappingsAsync,
  fetchCrmRequiredFilesAsync,
  configureCrmJobAsync,
  validateApiCredentialsAsync,
  configureApiCrmJobAsync,
} from './services';

const syncEntityFromCrmInstances = (state) => {
  const primaryEntity = primaryCrmEntity(state.crmInstances);
  if (primaryEntity) {
    state.entity = primaryEntity;
  }
};

const resetCrmState = (state) => {
  state.crmInstances = emptyCrmInstances();
  state.crmRequiredFiles = [];
  state.crmRequiredStatus = 'idle';
  state.recordCounts = null;
  state.imagesUrlExpired = false;
  state.imagesExpiredCounts = null;
  state.importWithoutImages = true;
  state.uploadRejections = [];
  state.crmConfigStatus = 'idle';
  state.crmConfigParseStatus = 'idle';
  state.crmConfigParseError = null;
  state.apiKey = '';
  state.apiCredentialsStatus = 'idle';
};

// Anything that changes what is being imported invalidates the accepted bundle:
// it has to be configured, validated and counted again before the confirm step.
const invalidateCrmBundle = (state) => {
  state.crmConfigStatus = 'idle';
  state.crmConfigParseStatus = 'idle';
  state.crmConfigParseError = null;
  state.recordCounts = null;
  state.imagesUrlExpired = false;
  state.imagesExpiredCounts = null;
  state.importWithoutImages = true;
};

const discardCrmDraftJob = (state) => {
  state.jobId = null;
  state.files = [];
  state.uploadResults = [];
  state.uploadRejections = [];
  state.recordCount = null;
  state.uploadStatus = 'idle';
};

// A rejected upload is remembered by name so the checklist can mark it, and
// forgotten as soon as a file with that name is accepted.
const applyUploadRejections = (state, uploadedFiles) => {
  const retried = new Set(uploadedFiles.map((file) => file.filename));
  state.uploadRejections = state.uploadRejections
    .filter((rejection) => !retried.has(rejection.filename))
    .concat(
      uploadedFiles
        .filter((file) => file.error)
        .map((file) => ({ filename: file.filename, reason: file.reason }))
    );
};

const resetValueMappingState = (state) => {
  state.valueMappings = null;
  state.valueMappingsStatus = 'idle';
  state.valueMappingsSaveStatus = 'idle';
  state.valueMappingsError = null;
  state.readyForStart = false;
  state.agentMappingsDraft = {};
  state.leadStatusMappingsDraft = {};
  state.agentDefaultDraft = null;
  state.leadStatusDefaultDraft = null;
  state.agentMappingsSaveRequestId = null;
  state.leadStatusMappingsSaveRequestId = null;
};

const resetMappingState = (state) => {
  state.mapping = { ...initialMappingState };
  state.mappingStatus = 'idle';
  state.mappingSaveStatus = 'idle';
  state.mappingSavingSourceColumnIndex = null;
  state.mappingRowsBackup = null;
  // Value mappings are read from the mapped columns, so dropping the column
  // mapping always drops them too.
  resetValueMappingState(state);
};

// Everything the wizard collected for a job that is no longer the one being
// configured. Kept in one place so a new piece of draft state cannot be
// forgotten by one of the four entry points that start over.
const resetJobDraft = (state) => {
  state.origin = null;
  state.files = [];
  state.jobId = null;
  state.uploadResults = [];
  state.recordCount = null;
  state.uploadStatus = 'idle';
  state.startStatus = 'idle';
  state.submitError = null;
  state.submitErrorReason = null;
  state.importSettingsDraft = {};
  resetMappingState(state);
  resetCrmState(state);
};

const seedValueMappingDrafts = (state, payload) => {
  const agents = payload?.agents;
  const leadStatus = payload?.lead_status;
  if (agents) {
    const agentDraft = {};
    (agents.mappings ?? []).forEach((item) => {
      agentDraft[item.source_value] = item.target_user_id;
    });
    // The backend suggests an agent per source value. It only pre-selects the
    // dropdown where there is no saved mapping yet, and is ignored when it
    // points to a user missing from the options (the row would look mapped
    // while the dropdown showed the placeholder).
    const agentOptionIds = new Set((agents.options ?? []).map((o) => o.id));
    (agents.source_values ?? []).forEach((source) => {
      const sourceValue = source.normalized;
      if (
        agentDraft[sourceValue] == null &&
        agentOptionIds.has(source.suggested_user_id)
      ) {
        agentDraft[sourceValue] = source.suggested_user_id;
      }
    });
    state.agentMappingsDraft = agentDraft;
    state.agentDefaultDraft = agents.default_user_id ?? null;
  }
  if (leadStatus) {
    const leadDraft = {};
    (leadStatus.mappings ?? []).forEach((item) => {
      leadDraft[item.source_value] = item.target_lead_status_id;
    });
    const defaultId = leadStatus.default_lead_status_id ?? null;
    // Column-mode empty rows are persisted as default_lead_status_id, not as
    // a mapping with an empty source_value. Rehydrate that row so the
    // dropdown stays selected after fetch/save.
    if (
      leadStatus.mode !== 'default' &&
      defaultId != null &&
      leadDraft[''] == null
    ) {
      const hasEmptySource = (leadStatus.source_values ?? []).some(
        (source) => (source.normalized ?? '') === ''
      );
      if (hasEmptySource) {
        leadDraft[''] = defaultId;
      }
    }
    state.leadStatusMappingsDraft = leadDraft;
    state.leadStatusDefaultDraft = defaultId;
  }
};

export const ImporterSlice = createSlice({
  name: 'Importer',
  initialState,
  reducers: {
    setStatusFilter(state, action) {
      state.statusFilter = action.payload;
      state.page = 1;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    openJobDetail(state, action) {
      state.detailJobId = action.payload;
    },
    closeJobDetail(state) {
      state.detailJobId = null;
    },
    moveStep(state, action) {
      if (action.payload === 'next') {
        state.activeStep += 1;
      } else {
        state.activeStep = Math.max(1, state.activeStep - 1);
      }
    },
    goToStep(state, action) {
      state.activeStep = Math.max(1, action.payload);
    },
    setEntity(state, action) {
      // Only reset wizard inputs when the entity actually changes, so returning
      // to step 1 and re-selecting the same entity preserves files/link.
      if (state.entity !== action.payload) {
        resetJobDraft(state);
      }
      state.entity = action.payload;
    },
    setMode(state, action) {
      // Only reset wizard inputs when the mode actually changes, so returning
      // to step 1 and re-selecting the same mode preserves files/link.
      if (state.mode !== action.payload) {
        resetJobDraft(state);
      }
      state.mode = action.payload;
    },
    setOrigin(state, action) {
      resetJobDraft(state);
      state.origin = action.payload;
    },
    toggleCrmInstance(state, action) {
      const entity = action.payload;
      state.crmInstances = {
        ...state.crmInstances,
        [entity]: !state.crmInstances[entity],
      };
      // The required workbooks depend on the selection, so the previous list no
      // longer applies either.
      state.crmRequiredFiles = [];
      state.crmRequiredStatus = 'idle';
      invalidateCrmBundle(state);
      discardCrmDraftJob(state);
      state.apiCredentialsStatus = 'idle';
      resetMappingState(state);
    },
    setApiKey(state, action) {
      if (state.apiKey !== action.payload) {
        state.apiCredentialsStatus = 'idle';
        invalidateCrmBundle(state);
        discardCrmDraftJob(state);
      }
      state.apiKey = action.payload;
    },
    resetMappingFetch(state) {
      state.mappingStatus = 'idle';
    },
    setImportWithoutImages(state, action) {
      state.importWithoutImages = Boolean(action.payload);
    },
    setAgentMappingDraft(state, action) {
      const { sourceValue, targetUserId } = action.payload;
      state.agentMappingsDraft = {
        ...state.agentMappingsDraft,
        [sourceValue]: targetUserId,
      };
    },
    setAgentDefaultDraft(state, action) {
      state.agentDefaultDraft = action.payload;
    },
    setLeadStatusMappingDraft(state, action) {
      const { sourceValue, targetLeadStatusId } = action.payload;
      state.leadStatusMappingsDraft = {
        ...state.leadStatusMappingsDraft,
        [sourceValue]: targetLeadStatusId,
      };
    },
    setLeadStatusDefaultDraft(state, action) {
      state.leadStatusDefaultDraft = action.payload;
    },
    setImportSettingDraft(state, action) {
      const { key, value } = action.payload;
      state.importSettingsDraft = {
        ...state.importSettingsDraft,
        [key]: value,
      };
    },
    setImportSettingsDraft(state, action) {
      state.importSettingsDraft = {
        ...state.importSettingsDraft,
        ...action.payload,
      };
    },
    addFiles(state, action) {
      const existing = new Set(state.files.map((f) => `${f.name}:${f.size}`));
      const uploadedNames = new Set(
        state.uploadResults.map((result) => result.filename).filter(Boolean)
      );
      action.payload.forEach((file) => {
        const key = `${file.name}:${file.size}`;
        if (!existing.has(key) && !uploadedNames.has(file.name)) {
          existing.add(key);
          state.files.push(file);
        }
      });
    },
    removeFile(state, action) {
      state.files.splice(action.payload, 1);
    },
    // The backend already dropped the file; this only clears the row the user
    // is looking at, which unticks it on the required files checklist.
    removeUploadRejection(state, action) {
      state.uploadRejections.splice(action.payload, 1);
    },
    resetWizard(state) {
      state.activeStep = 1;
      state.entity = null;
      state.mode = null;
      resetJobDraft(state);
    },
    // Dispatched by useCrmConfigPolling once the async bundle parse enqueued
    // by configureCrmJobAsync finishes: the counts only become known now,
    // never from the POST's own (immediate, RUNNING) response.
    crmConfigParseFinished(state, action) {
      const { recordCounts, mediaExpired, imagesExpiredCounts } =
        action.payload;
      state.crmConfigParseStatus = 'finished';
      state.crmConfigParseError = null;
      state.recordCounts = recordCounts ?? null;
      state.imagesUrlExpired = Boolean(mediaExpired);
      state.imagesExpiredCounts = imagesExpiredCounts ?? null;
      state.importWithoutImages = true;
      syncEntityFromCrmInstances(state);
      state.activeStep += 1;
    },
    crmConfigParseFailed(state, action) {
      state.crmConfigParseStatus = 'error';
      state.crmConfigParseError = action.payload ?? null;
      // Reuses the existing submitError toast effect so a background parse
      // failure surfaces the same way a synchronous request error used to.
      state.submitError = action.payload ?? 'crm_config_parse_failed';
      state.submitErrorReason = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchJobsAsync.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.jobs = action.payload?.jobs ?? [];
        state.counts = action.payload?.counts ?? state.counts;
        state.pagination = action.payload?.pagination ?? state.pagination;
        // Out-of-range page (e.g. totals shrank): snap back to page 1 so the
        // user isn't stranded on an empty page. The page change triggers a
        // refetch via the dashboard effect.
        if (
          state.jobs.length === 0 &&
          state.page > 1 &&
          state.pagination.total_items > 0
        ) {
          state.page = 1;
        }
      })
      .addCase(fetchJobsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(retryJobAsync.pending, (state, action) => {
        if (!state.retryingIds.includes(action.meta.arg)) {
          state.retryingIds.push(action.meta.arg);
        }
      })
      .addCase(retryJobAsync.fulfilled, (state, action) => {
        state.retryingIds = state.retryingIds.filter(
          (id) => id !== action.meta.arg
        );
      })
      .addCase(retryJobAsync.rejected, (state, action) => {
        state.retryingIds = state.retryingIds.filter(
          (id) => id !== action.meta.arg
        );
      })
      .addCase(downloadContactsExportAsync.pending, (state, action) => {
        const { id } = action.meta.arg;
        if (!state.contactsExportIds.includes(id)) {
          state.contactsExportIds.push(id);
        }
      })
      .addCase(downloadContactsExportAsync.fulfilled, (state, action) => {
        const { id } = action.meta.arg;
        state.contactsExportIds = state.contactsExportIds.filter(
          (jobId) => jobId !== id
        );
      })
      .addCase(downloadContactsExportAsync.rejected, (state, action) => {
        const { id } = action.meta.arg;
        state.contactsExportIds = state.contactsExportIds.filter(
          (jobId) => jobId !== id
        );
      })
      .addCase(uploadFilesAsync.pending, (state) => {
        state.uploadStatus = 'loading';
        state.submitError = null;
      })
      .addCase(uploadFilesAsync.fulfilled, (state, action) => {
        state.uploadStatus = 'succeeded';
        state.jobId = action.payload?.job_id ?? state.jobId;
        state.recordCount = action.payload?.record_count ?? state.recordCount;
        const uploadedFiles = action.payload?.files ?? [];
        applyUploadRejections(state, uploadedFiles);
        state.uploadResults = state.uploadResults.concat(
          // A rejected file is deleted by the backend, so it must not show up as
          // uploaded; the wizard reads the errors off the response instead.
          uploadedFiles.filter((file) => !file.error)
        );
        state.files = [];
        resetMappingState(state);
        const savedPolicies = parseImportSettingsFromJob(action.payload);
        if (Object.keys(savedPolicies).length > 0) {
          state.importSettingsDraft = {
            ...state.importSettingsDraft,
            ...savedPolicies,
          };
        }
        // A CRM bundle still has to be configured and validated before the
        // confirm step, so the wizard advances once `crm-config` succeeds.
        if (!isCrmMode(state.mode)) {
          state.activeStep += 1;
        }
      })
      .addCase(uploadFilesAsync.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(fetchCrmRequiredFilesAsync.pending, (state) => {
        state.crmRequiredStatus = 'loading';
        state.submitError = null;
      })
      .addCase(fetchCrmRequiredFilesAsync.fulfilled, (state, action) => {
        state.crmRequiredStatus = 'succeeded';
        state.crmRequiredFiles = action.payload ?? [];
      })
      .addCase(fetchCrmRequiredFilesAsync.rejected, (state, action) => {
        state.crmRequiredStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(configureCrmJobAsync.pending, (state) => {
        state.crmConfigStatus = 'loading';
        state.crmConfigParseStatus = 'idle';
        state.crmConfigParseError = null;
        state.submitError = null;
      })
      .addCase(configureCrmJobAsync.fulfilled, (state) => {
        // Bundle parsing runs async on the backend: the POST only confirms
        // the job was enqueued (status RUNNING). Counts and the step advance
        // arrive later via crmConfigParseFinished, dispatched by
        // useCrmConfigPolling once GET /jobs/{id}/ reports a terminal state.
        state.crmConfigStatus = 'succeeded';
        state.crmConfigParseStatus = 'running';
      })
      .addCase(configureCrmJobAsync.rejected, (state, action) => {
        state.crmConfigStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(validateApiCredentialsAsync.pending, (state) => {
        state.apiCredentialsStatus = 'loading';
        state.submitError = null;
      })
      .addCase(validateApiCredentialsAsync.fulfilled, (state) => {
        state.apiCredentialsStatus = 'succeeded';
      })
      .addCase(validateApiCredentialsAsync.rejected, (state, action) => {
        state.apiCredentialsStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(configureApiCrmJobAsync.pending, (state) => {
        state.uploadStatus = 'loading';
        state.submitError = null;
      })
      .addCase(configureApiCrmJobAsync.fulfilled, (state, action) => {
        const { payload } = action;
        state.uploadStatus = 'succeeded';
        state.jobId = payload?.job_id ?? state.jobId;
        state.recordCount = payload?.record_count ?? state.recordCount;
        state.recordCounts = payload?.record_counts ?? null;
        state.imagesUrlExpired = payload?.images_url_expired ?? false;
        state.imagesExpiredCounts = payload?.images_expired_counts ?? null;
        state.importWithoutImages = true;
        syncEntityFromCrmInstances(state);
        state.activeStep += 1;
      })
      .addCase(configureApiCrmJobAsync.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(startImportAsync.pending, (state) => {
        state.startStatus = 'loading';
        state.submitError = null;
      })
      .addCase(startImportAsync.fulfilled, (state) => {
        state.startStatus = 'succeeded';
        state.activeStep += 1;
      })
      .addCase(startImportAsync.rejected, (state, action) => {
        state.startStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(removeUploadedFileAsync.fulfilled, (state, action) => {
        const { index, fileId } = action.payload;
        const target =
          fileId != null
            ? state.uploadResults.findIndex((r) => r.file_id === fileId)
            : index;
        if (
          target >= 0 &&
          target < state.uploadResults.length &&
          (fileId == null || state.uploadResults[target]?.file_id === fileId)
        ) {
          state.uploadResults.splice(target, 1);
          const hasFileEntries = state.uploadResults.some(
            (result) => result.file_id != null && !result.error
          );
          state.recordCount = hasFileEntries
            ? sumFileRecordCount(state.uploadResults)
            : null;
          // The removed file is what the fetched mapping was built from, so it
          // is dropped to avoid mapping the columns of a file that is gone.
          resetMappingState(state);
          invalidateCrmBundle(state);
        }
      })
      .addCase(fetchJobLiveInfoAsync.fulfilled, (state, action) => {
        const liveInfo = action.payload;
        if (!liveInfo) return;
        const job = state.jobs.find((item) => item.id === liveInfo.id);
        if (!job) return;
        // Guard against out-of-order polls: never regress a terminal job
        // (done/error) back to in_progress. A slow, stale response could
        // otherwise overwrite a newer terminal status and its record count.
        const isStaleRegression =
          job.status !== JOB_STATUS.IN_PROGRESS &&
          liveInfo.status === JOB_STATUS.IN_PROGRESS;
        if (isStaleRegression) return;
        // Only apply present values so a partial live_info never wipes the row.
        if (liveInfo.status) job.status = liveInfo.status;
        if (liveInfo.records != null) job.records = liveInfo.records;
        // On a filtered tab, drop a job that no longer matches the active
        // filter so it doesn't linger with a mismatched badge until the
        // completion refetch reconciles the list.
        if (
          state.statusFilter !== STATUS_FILTER.ALL &&
          job.status !== state.statusFilter
        ) {
          state.jobs = state.jobs.filter((item) => item.id !== job.id);
        }
      })
      .addCase(fetchMappingAsync.pending, (state) => {
        state.mappingStatus = 'loading';
        state.submitError = null;
      })
      .addCase(fetchMappingAsync.fulfilled, (state, action) => {
        state.mappingStatus = 'succeeded';
        const recordCount =
          action.payload.recordCount ??
          state.mapping.recordCount ??
          state.recordCount;
        state.mapping = {
          rows: action.payload.rows,
          catalog: action.payload.catalog,
          counts: action.payload.counts,
          recordCount,
          validation: action.payload.validation,
        };
        if (action.payload.recordCount != null) {
          state.recordCount = action.payload.recordCount;
        }
      })
      .addCase(fetchMappingAsync.rejected, (state, action) => {
        state.mappingStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(saveMappingAsync.pending, (state, action) => {
        state.mappingSaveStatus = 'loading';
        state.submitError = null;
        state.mappingRowsBackup = state.mapping.rows;
        if (action.meta.arg?.ignoreUnmapped) {
          state.mappingSavingSourceColumnIndex = null;
          state.mapping.rows = ignoreReviewRows(
            state.mapping.rows,
            state.mapping.catalog
          );
          return;
        }
        const { sourceColumnIndex, targetField } = action.meta.arg;
        state.mappingSavingSourceColumnIndex = sourceColumnIndex;
        state.mapping.rows = applyTargetFieldChange(
          state.mapping.rows,
          sourceColumnIndex,
          targetField,
          state.mapping.catalog
        );
      })
      .addCase(saveMappingAsync.fulfilled, (state, action) => {
        state.mappingSaveStatus = 'succeeded';
        state.mappingSavingSourceColumnIndex = null;
        state.mappingRowsBackup = null;
        // The source values offered by each value mapping block come from the
        // columns just remapped, so they are dropped and refetched on entry.
        resetValueMappingState(state);
        const recordCount =
          action.payload.recordCount ??
          state.mapping.recordCount ??
          state.recordCount;
        state.mapping = {
          rows: action.payload.rows,
          catalog: action.payload.catalog,
          counts: action.payload.counts,
          recordCount,
          validation: action.payload.validation,
        };
      })
      .addCase(saveMappingAsync.rejected, (state, action) => {
        state.mappingSaveStatus = 'failed';
        state.mappingSavingSourceColumnIndex = null;
        if (state.mappingRowsBackup) {
          state.mapping.rows = state.mappingRowsBackup;
          state.mappingRowsBackup = null;
        }
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(fetchValueMappingsAsync.pending, (state) => {
        state.valueMappingsStatus = 'loading';
        state.valueMappingsError = null;
      })
      .addCase(fetchValueMappingsAsync.fulfilled, (state, action) => {
        state.valueMappingsStatus = 'succeeded';
        state.valueMappings = action.payload;
        state.readyForStart = action.payload?.readyForStart ?? false;
        seedValueMappingDrafts(state, action.payload);
      })
      .addCase(fetchValueMappingsAsync.rejected, (state, action) => {
        state.valueMappingsStatus = 'failed';
        state.valueMappingsError =
          action.payload?.message ?? action.error?.message ?? null;
      })
      .addCase(saveAgentMappingsAsync.pending, (state, action) => {
        state.valueMappingsSaveStatus = 'loading';
        state.submitError = null;
        state.agentMappingsSaveRequestId = action.meta.requestId;
      })
      .addCase(saveAgentMappingsAsync.fulfilled, (state, action) => {
        // Saving now fires on every dropdown change instead of being gated by
        // this same status, so an older save can still be in flight when a
        // newer one resolves first. Only the most recently dispatched request
        // gets applied — an older one's response was built from a draft that
        // doesn't know about edits made after it was sent, and would wipe them.
        if (action.meta.requestId !== state.agentMappingsSaveRequestId) return;
        state.valueMappingsSaveStatus = 'succeeded';
        state.valueMappings = { ...state.valueMappings, ...action.payload };
        state.readyForStart = action.payload?.readyForStart ?? false;
        seedValueMappingDrafts(state, action.payload);
      })
      .addCase(saveAgentMappingsAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.agentMappingsSaveRequestId) return;
        state.valueMappingsSaveStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      })
      .addCase(saveLeadStatusMappingsAsync.pending, (state, action) => {
        state.valueMappingsSaveStatus = 'loading';
        state.submitError = null;
        state.leadStatusMappingsSaveRequestId = action.meta.requestId;
      })
      .addCase(saveLeadStatusMappingsAsync.fulfilled, (state, action) => {
        // Same "latest request wins" guard as saveAgentMappingsAsync above.
        if (action.meta.requestId !== state.leadStatusMappingsSaveRequestId) {
          return;
        }
        state.valueMappingsSaveStatus = 'succeeded';
        state.valueMappings = { ...state.valueMappings, ...action.payload };
        state.readyForStart = action.payload?.readyForStart ?? false;
        seedValueMappingDrafts(state, action.payload);
        // Saving now also happens on every dropdown change (not just Next), so
        // advancing here would skip to the next step as soon as the last value
        // gets mapped. The step only moves forward when Next is clicked —
        // see handleSaveLeadStatus in ImporterWizard.
      })
      .addCase(saveLeadStatusMappingsAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.leadStatusMappingsSaveRequestId) {
          return;
        }
        state.valueMappingsSaveStatus = 'failed';
        state.submitError =
          action.payload?.message ?? action.error?.message ?? null;
        state.submitErrorReason = action.payload?.reason ?? null;
      });
  },
});

export const {
  setStatusFilter,
  setPage,
  openJobDetail,
  closeJobDetail,
  moveStep,
  goToStep,
  setEntity,
  setMode,
  setOrigin,
  addFiles,
  removeFile,
  removeUploadRejection,
  resetWizard,
  toggleCrmInstance,
  setApiKey,
  resetMappingFetch,
  setImportWithoutImages,
  setAgentMappingDraft,
  setAgentDefaultDraft,
  setLeadStatusMappingDraft,
  setLeadStatusDefaultDraft,
  setImportSettingDraft,
  setImportSettingsDraft,
  crmConfigParseFinished,
  crmConfigParseFailed,
} = ImporterSlice.actions;

export {
  fetchJobsAsync,
  retryJobAsync,
  downloadContactsExportAsync,
  uploadFilesAsync,
  startImportAsync,
  removeUploadedFileAsync,
  fetchJobLiveInfoAsync,
  fetchMappingAsync,
  saveMappingAsync,
  fetchValueMappingsAsync,
  saveAgentMappingsAsync,
  saveLeadStatusMappingsAsync,
};

export const selectImporterState = (state) => state.ImporterSlice;

export default ImporterSlice.reducer;
